/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import { DiagComponentLogger } from '../diag/ComponentLogger';
import { createLogLevelDiagLogger } from '../diag/internal/logLevelLogger';
import type {
  ComponentLoggerOptions,
  DiagLogFunction,
  DiagLogger,
  DiagLoggerApi,
} from '../diag/types';
import { DiagLogLevel } from '../diag/types';
import {
  getGlobal,
  registerGlobal,
  unregisterGlobal,
} from '../internal/global-utils';

const API_NAME = 'diag';

/**
 * Singleton object which represents the entry point to the OpenTelemetry internal
 * diagnostic API
 *
 * @since 1.0.0
 */
export class DiagAPI implements DiagLogger, DiagLoggerApi {
  private static _instance?: DiagAPI;

  /** Get the singleton instance of the DiagAPI API */
  public static instance(): DiagAPI {
    if (!this._instance) {
      this._instance = new DiagAPI();
    }

    return this._instance;
  }

  /**
   * Private internal constructor
   * @private
   */
  private constructor() {
    function _logProxy(funcName: keyof DiagLogger): DiagLogFunction {
      return function (...args) {
        const logger = getGlobal('diag');
        // shortcut if logger not set
        if (!logger) return;
        return logger[funcName](...args);
      };
    }

    // Using self local variable for minification purposes as 'this' cannot be minified
    const self = this;

    // DiagAPI specific functions

    const setLogger: DiagLoggerApi['setLogger'] = (
      logger,
      optionsOrLogLevel = { logLevel: DiagLogLevel.INFO }
    ) => {
      if (logger === self) {
        // There isn't much we can do here.
        // Logging to the console might break the user application.
        // Try to log to self. If a logger was previously registered it will receive the log.
        const err = new Error(
          'Cannot use diag as the logger for itself. Please use a DiagLogger implementation like ConsoleDiagLogger or a custom implementation'
        );
        self.error(err.stack ?? err.message);
        return false;
      }

      if (typeof optionsOrLogLevel === 'number') {
        optionsOrLogLevel = {
          logLevel: optionsOrLogLevel,
        };
      }

      const oldLogger = getGlobal('diag');
      const newLogger = createLogLevelDiagLogger(
        optionsOrLogLevel.logLevel ?? DiagLogLevel.INFO,
        logger
      );
      // There already is an logger registered. We'll let it know before overwriting it.
      if (oldLogger && !optionsOrLogLevel.suppressOverrideMessage) {
        const stack = new Error().stack ?? '<failed to generate stacktrace>';
        oldLogger.warn(`Current logger will be overwritten from ${stack}`);
        newLogger.warn(
          `Current logger will overwrite one already registered from ${stack}`
        );
      }

      return registerGlobal('diag', newLogger, self, true);
    };

    self.setLogger = setLogger;

    self.disable = () => {
      unregisterGlobal(API_NAME, self);
    };

    self.createComponentLogger = (options: ComponentLoggerOptions) => {
      return new DiagComponentLogger(options);
    };

    self.verbose = _logProxy('verbose');
    self.debug = _logProxy('debug');
    self.info = _logProxy('info');
    self.warn = _logProxy('warn');
    self.error = _logProxy('error');
  }

  public setLogger!: DiagLoggerApi['setLogger'];
  /**
   *
   */
  public createComponentLogger!: (
    options: ComponentLoggerOptions
  ) => DiagLogger;

  // DiagLogger implementation
  public verbose!: DiagLogFunction;
  public debug!: DiagLogFunction;
  public info!: DiagLogFunction;
  public warn!: DiagLogFunction;
  public error!: DiagLogFunction;

  /**
   * Unregister the global logger and return to Noop
   */
  public disable!: () => void;
}



// WEBPACK FOOTER //
// ../../../src/api/diag.ts